import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { Points } from 'components/Points/Points';
import { RECENT_ACTIVITY_COUNT, SEND_REQUIRES_ADDRESS, TOAST_UNDO_MS } from 'constants/home';
import { useGetActivityMapQuery, useGetHomeActivityStatsQuery, useGetRecentActivityQuery } from 'store/services/activity/activityApi';
import { useResendVerificationMutation } from 'store/services/auth/authApi';
import {
  useClaimOnboardingPointsMutation,
  useConfirmOnboardingTaskMutation,
  useDismissOnboardingMutation,
  useGetOnboardingChecklistQuery,
  useUndismissOnboardingMutation,
} from 'store/services/onboarding/onboardingApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import { trackEvent } from 'utils/analytics';
import { computeAffordability } from './affordability';
import FeaturedTask from './FeaturedTask';
import SetupLadder from './SetupLadder';
import { arrivesHint, featuredCopy, HIDDEN_TOAST, SETUP_COMPLETE_BODY_AFTER, SETUP_COMPLETE_BODY_BEFORE, SETUP_COMPLETE_TITLE, TAGS, UNDO } from './setupCopy';
import { barSegments, deriveChipStates, isClaimed, pickFeatured, setupTotals, sortChipKeys, WANT_TARGETS, checklistPhoneVerified, isPhoneGateError } from './setupState';
import './SetupBanner.css';

// The Setup Banner (Asana 1218957945501621, Decisions 11 to 18). One featured
// task on the left, the full ladder on the right, every open chip clickable.
// Claim all is N sequential calls to the EXISTING claim endpoint behind a
// synchronous in-flight lock: no new claim endpoint, no new points math.

const SOCIAL_TARGET: Record<'FOLLOW_TWITTER' | 'FOLLOW_BLUESKY', string> = {
  FOLLOW_TWITTER: 'https://x.com/trademagicgg',
  FOLLOW_BLUESKY: 'https://bsky.app/profile/trademagic.gg',
};

export interface SetupBannerProps {
  onVerifyPhone: () => void;
  /** A claim landed: the first-claim celebration lives in HomePage */
  onClaimed: (taskKey: OnboardingTaskKey, points: number) => void;
}

const SetupBanner: React.FC<SetupBannerProps> = ({ onVerifyPhone, onClaimed }) => {
  const navigate = useNavigate();
  const { data: checklist, isLoading, refetch } = useGetOnboardingChecklistQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: stats } = useGetHomeActivityStatsQuery();
  const { data: me } = useGetMeQuery();
  const { data: recent } = useGetRecentActivityQuery({ limit: 54 });
  const [claimPoints] = useClaimOnboardingPointsMutation();
  const [confirmTask, { isLoading: confirming }] = useConfirmOnboardingTaskMutation();
  const [dismissOnboarding] = useDismissOnboardingMutation();
  const [undismissOnboarding] = useUndismissOnboardingMutation();
  const [resendVerification, { isLoading: resending }] = useResendVerificationMutation();

  const [pickedKey, setPickedKey] = useState<OnboardingTaskKey | null>(null);
  const [confirmingKey, setConfirmingKey] = useState<OnboardingTaskKey | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [completedThisSession, setCompletedThisSession] = useState(false);
  const [hiddenLocally, setHiddenLocally] = useState(false);
  const claimLockRef = useRef(false);

  const tasks = useMemo(() => checklist?.tasks ?? [], [checklist]);
  const wantListCount = checklist?.wantListCount ?? tasks.find((t) => t.taskKey === 'ADD_WANT_50')?.progress?.current ?? 0;
  // The live phone flag from the payload, never the VERIFY_PHONE row (pass 7)
  const phoneVerified = checklistPhoneVerified(checklist);
  const chips = useMemo(() => deriveChipStates(tasks, wantListCount, SEND_REQUIRES_ADDRESS, phoneVerified), [tasks, wantListCount, phoneVerified]);
  const pick = useMemo(() => pickFeatured(tasks, chips, wantListCount, pickedKey, phoneVerified), [tasks, chips, wantListCount, pickedKey, phoneVerified]);
  const totals = useMemo(() => setupTotals(tasks, chips), [tasks, chips]);
  const sortedKeys = useMemo(() => sortChipKeys(tasks, chips), [tasks, chips]);
  const segments = useMemo(() => barSegments(tasks, chips, pick.taskKey), [tasks, chips, pick.taskKey]);
  const byKey = useMemo(() => new Map(tasks.map((t) => [t.taskKey, t])), [tasks]);
  const featuredTask = pick.taskKey ? byKey.get(pick.taskKey) ?? null : null;

  // The map's personal rows carry the state F and G facts (ETA, recipient); fetched only then
  const needsMine = pick.state === 'F' || pick.state === 'G';
  const { data: map } = useGetActivityMapQuery(undefined, { skip: !needsMine });
  const soonestOutbound = useMemo(() => {
    const rows = (map?.mine?.inTransit ?? []).filter((r) => r.direction === 'outbound' && r.estimatedDeliveryBy);
    return rows.sort((a, b) => (a.estimatedDeliveryBy! < b.estimatedDeliveryBy! ? -1 : 1))[0] ?? (map?.mine?.inTransit ?? [])[0] ?? null;
  }, [map]);

  const copy = useMemo(
    () =>
      featuredCopy({
        state: pick.state,
        taskKey: pick.taskKey,
        picked: pick.picked,
        readyKeys: pick.readyKeys,
        tasks,
        wantListCount,
        counterpartyUsername: soonestOutbound?.counterpartyUsername ?? null,
        arrivesHint: arrivesHint(soonestOutbound?.estimatedDeliveryBy, pick.state),
      }),
    [pick, tasks, wantListCount, soonestOutbound]
  );

  const affordability = useMemo(() => {
    const items = (recent?.activities ?? [])
      .slice(0, RECENT_ACTIVITY_COUNT)
      .filter((a) => a.pointValue !== null && a.pointValue > 0)
      .map((a) => ({ id: a.id, name: a.card.name, pointValue: a.pointValue as number, imageUrl: a.card.imageUrl }));
    return computeAffordability({
      items,
      budget: totals.remainingPoints,
      unclaimed: totals.readyPoints,
      remaining: totals.unfinishedPoints,
      median: stats?.medianCardPointsThisWeek ?? 0,
    });
  }, [recent, totals, stats]);

  // The server flag is the truth. A local hide collapses the slot before the
  // dismiss round-trips; an undismiss from ANY surface (the toast's Undo, or
  // Finish setup in the profile menu) shows up as the refetched flag going null,
  // which clears the local hide too.
  useEffect(() => {
    if (checklist && !checklist.onboardingDismissedAt) setHiddenLocally(false);
  }, [checklist?.onboardingDismissedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legacy cache from the old checklist: the server flag is the only truth now
  useEffect(() => {
    try {
      localStorage.removeItem('onboardingDismissed');
    } catch {
      /* storage unavailable */
    }
  }, []);

  // ---- actions

  const runClaims = useCallback(
    async (requested: OnboardingTaskKey[]) => {
      // Mobile auto-credits on verify (ruling 2026-09-28): it is never claimed from here
      const keys = requested.filter((key) => key !== 'VERIFY_PHONE');
      if (claimLockRef.current || keys.length === 0) return;
      claimLockRef.current = true;
      setClaiming(true);
      try {
        for (const key of keys) {
          const result = await claimPoints({ taskKey: key }).unwrap();
          const points = typeof result?.pointsAwarded === 'number' ? result.pointsAwarded : byKey.get(key)?.pointsReward ?? 0;
          trackEvent('onboarding_points_claimed', { task_key: key, points_awarded: points });
          onClaimed(key, points);
        }
        const remainingAfter = tasks.filter((t) => !isClaimed(t) && !keys.includes(t.taskKey));
        if (remainingAfter.length === 0) setCompletedThisSession(true);
      } catch (error: any) {
        if (isPhoneGateError(error)) {
          // The server says the phone is not verified: refetch and show the
          // gated state instead of a toast (pass 7)
          trackEvent('onboarding_claim_phone_gated');
          refetch();
        } else {
          toast.error(error?.data?.message || error?.data?.error || 'Failed to claim points. Please try again.');
        }
      } finally {
        claimLockRef.current = false;
        setClaiming(false);
      }
    },
    [claimPoints, byKey, onClaimed, tasks, refetch]
  );

  const openSocial = useCallback((key: 'FOLLOW_TWITTER' | 'FOLLOW_BLUESKY') => {
    const task = byKey.get(key);
    const target = task?.action?.target || SOCIAL_TARGET[key];
    window.open(target, '_blank', 'noopener,noreferrer');
    setConfirmingKey(key);
    trackEvent('onboarding_task_started', { task_key: key, action_type: 'EXTERNAL_LINK' });
  }, [byKey]);

  const startTask = useCallback(
    (task: OnboardingTask) => {
      trackEvent('onboarding_task_started', { task_key: task.taskKey, task_title: task.title, action_type: task.action?.type });
      switch (task.taskKey) {
        case 'VERIFY_PHONE':
          onVerifyPhone();
          return;
        case 'VERIFY_EMAIL':
          if (!me?.email) {
            navigate('/settings');
            return;
          }
          resendVerification({ email: me.email })
            .unwrap()
            .then(() => toast.success('Verification email sent. Check your inbox.'))
            .catch((error: any) => toast.error(error?.data?.message || 'Could not send the verification email. Please try again.'));
          return;
        case 'FOLLOW_TWITTER':
        case 'FOLLOW_BLUESKY':
          openSocial(task.taskKey);
          return;
        default:
          if (task.action?.type === 'EXTERNAL_LINK') {
            window.open(task.action.target, '_blank', 'noopener,noreferrer');
          } else if (task.action?.target) {
            navigate(task.action.target);
          }
      }
    },
    [me, navigate, onVerifyPhone, openSocial, resendVerification]
  );

  const onPrimary = useCallback(() => {
    if (pick.state === 'C' || pick.state === 'H') {
      runClaims(pick.readyKeys);
      return;
    }
    if (pick.state === 'G') {
      navigate('/sending');
      return;
    }
    if (pick.state === 'I') {
      openSocial(byKey.get('FOLLOW_TWITTER')?.status === 'AVAILABLE' ? 'FOLLOW_TWITTER' : 'FOLLOW_BLUESKY');
      return;
    }
    if (featuredTask) startTask(featuredTask);
  }, [pick, runClaims, navigate, openSocial, byKey, featuredTask, startTask]);

  const onSecondary = useCallback(() => {
    if (pick.state === 'I') openSocial('FOLLOW_BLUESKY');
  }, [pick.state, openSocial]);

  const onConfirmFollow = useCallback(async () => {
    if (!confirmingKey) return;
    try {
      await confirmTask({ taskKey: confirmingKey }).unwrap();
      trackEvent('onboarding_task_completed', { task_key: confirmingKey });
      setConfirmingKey(null);
    } catch {
      toast.error('Failed to confirm. Please try again.');
    }
  }, [confirmingKey, confirmTask]);

  const onHide = useCallback(async () => {
    setHiddenLocally(true);
    try {
      await dismissOnboarding().unwrap();
      trackEvent('onboarding_dismissed');
    } catch {
      setHiddenLocally(false);
      toast.error('Could not hide the setup card. Please try again.');
      return;
    }
    const toastId = 'setup-hidden';
    toast.success(
      <span className="setup-hidden-toast">
        {HIDDEN_TOAST}{' '}
        <button
          type="button"
          className="setup-hidden-toast__undo"
          onClick={(e) => {
            e.stopPropagation();
            toast.dismiss(toastId);
            undismissOnboarding()
              .unwrap()
              .then(() => setHiddenLocally(false))
              .catch(() => toast.error('Could not bring the setup card back. Try Finish setup in your profile menu.'));
          }}
        >
          {UNDO}
        </button>
      </span>,
      { toastId, autoClose: TOAST_UNDO_MS }
    );
  }, [dismissOnboarding, undismissOnboarding]);

  const onPick = useCallback((task: OnboardingTask) => {
    setPickedKey(task.taskKey);
    setConfirmingKey(null);
  }, []);

  const onClaimChip = useCallback((task: OnboardingTask) => runClaims([task.taskKey]), [runClaims]);

  // ---- render gates

  if (isLoading && !checklist) {
    return <div className="setup-banner setup-banner--loading" data-testid="setup-banner-loading" aria-busy="true" />;
  }
  if (!checklist || tasks.length === 0) return null;
  if (checklist.onboardingDismissedAt || hiddenLocally) return null;

  if (pick.state === 'gone') {
    if (!completedThisSession) return null;
    return (
      <section className="setup-banner setup-banner--complete" data-testid="setup-banner" data-state="complete">
        <div className="setup-featured">
          <span className="setup-featured__tag setup-featured__tag--green">{TAGS.H}</span>
          <h2 className="setup-featured__title">{SETUP_COMPLETE_TITLE}</h2>
          <p className="setup-featured__body">
            {SETUP_COMPLETE_BODY_BEFORE}
            <Points type="inline" points={totals.totalPossible} />
            {SETUP_COMPLETE_BODY_AFTER}
          </p>
        </div>
      </section>
    );
  }

  const readySum = pick.readyKeys.reduce((sum, key) => sum + (byKey.get(key)?.pointsReward ?? 0), 0);
  // No reward pill for a phone that was verified before: the auto-credit's conditional update decides whether it pays again
  const rewardPoints = pick.state === 'A' && pick.reverify ? 0 : pick.state === 'C' || pick.state === 'H' ? readySum : pick.state === 'I' ? tasks.filter((t) => (t.taskKey === 'FOLLOW_TWITTER' || t.taskKey === 'FOLLOW_BLUESKY') && t.status === 'AVAILABLE').reduce((s, t) => s + t.pointsReward, 0) : featuredTask?.pointsReward ?? 0;
  const progress =
    pick.state === 'D' && featuredTask && WANT_TARGETS[featuredTask.taskKey] ? { current: Math.min(wantListCount, WANT_TARGETS[featuredTask.taskKey]!), target: WANT_TARGETS[featuredTask.taskKey]! } : null;
  const busy = claiming || confirming || resending;

  return (
    <section
      className="setup-banner"
      data-testid="setup-banner"
      data-state={pick.state}
      data-picked={pick.picked || undefined}
      data-gated={pick.state === 'A' || undefined}
      data-reverify={pick.reverify || undefined}
      aria-label="Setup"
    >
      {pick.state === 'I' && (
        <button type="button" className="setup-banner__close" aria-label="Hide the setup card" onClick={onHide} data-testid="setup-hide-close">
          ×
        </button>
      )}
      <FeaturedTask
        state={pick.state}
        copy={copy}
        task={featuredTask}
        rewardPoints={rewardPoints}
        rewardSigned={pick.state !== 'C' && pick.state !== 'H'}
        picked={pick.picked}
        busy={busy}
        confirmingKey={confirmingKey}
        progress={progress}
        actions={{
          onPrimary,
          onSecondary: pick.state === 'I' ? onSecondary : undefined,
          onBackToSuggestion: () => setPickedKey(null),
          onHide: pick.state === 'I' ? onHide : undefined,
          onConfirmFollow,
          onCancelFollow: () => setConfirmingKey(null),
        }}
      />
      <SetupLadder
        state={pick.state}
        tasks={tasks}
        chips={chips}
        sortedKeys={sortedKeys}
        segments={segments}
        totals={totals}
        featuredKey={pick.taskKey}
        addressGated={SEND_REQUIRES_ADDRESS}
        affordability={affordability}
        claiming={claiming}
        gated={pick.state === 'A'}
        onPick={onPick}
        onClaim={onClaimChip}
      />
    </section>
  );
};

export default SetupBanner;
